import { themeVar as colors } from "../design-tokens";
import type { FileReadStatus } from "./types";

/**
 * HOW EACH FILE WAS READ — AND BY WHOM (2026-08-04).
 *
 * Two things this closes, both of which had the same shape: the server computed the honest answer
 * and the client threw it away.
 *
 * 1. THE THIRD-PARTY DISCLOSURE, which is a stated product requirement and was reaching nobody.
 *    `amb_extract` sets `stage_note` to, in the agent's words, "This PDF's text was read by our
 *    document-conversion service (N page(s)) — the file was processed by a third-party OCR
 *    provider." `amb_report` puts it on the payload as `grid.files`. Nothing rendered it. An agent
 *    is entitled to know a third party handled their client's document, and the sentence existed
 *    the whole time on the wrong side of a missing type definition.
 *
 * 2. WHICH READER PRODUCED THE NUMBERS. The product's promise is that every figure traces to its
 *    source. Until this rendered, the reader itself was the one part nobody could trace — an
 *    operator could not tell a deterministic parse from a hosted OCR read from a local tesseract
 *    pass without a shell into the container, and the logs were silent too.
 *
 * DELIBERATELY CALM. This is a receipt, not a warning: nothing here is a problem the agent has to
 * act on. Only a genuine shortfall — pages that could not be read — is drawn in the caution colour,
 * because that one IS actionable and is exactly what a scanned page count is usually hiding.
 */
export function FileReadReceipt({ files }: { files?: FileReadStatus[] }) {
  if (!files || files.length === 0) return null;
  const thirdParty = files.some((f) => /third-party|third party/i.test(f.stage_note ?? ""));

  return (
    <section
      aria-labelledby="vb-file-read-receipt"
      style={{
        marginTop: 16, border: `1px solid ${colors.border}`, borderRadius: 8,
        padding: "12px 14px", background: colors.bgApp,
      }}
    >
      <h3 id="vb-file-read-receipt" style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
        How we read your files
      </h3>
      <ul style={{ margin: "6px 0 0", padding: 0, listStyle: "none", fontSize: 13, color: colors.textSecondary, lineHeight: 1.6 }}>
        {files.map((f, i) => {
          // A shortfall is the one line here worth colouring. `pages_total` can be absent on a
          // format that has no pages (a spreadsheet), so a missing count is never read as a loss.
          const short =
            typeof f.pages_read === "number" && typeof f.pages_total === "number"
              && f.pages_total > 0 && f.pages_read < f.pages_total;
          return (
            <li key={`${f.name}-${i}`} style={{ marginTop: i === 0 ? 0 : 8 }}>
              <span style={{ color: colors.textPrimary, fontWeight: 600 }}>{f.name}</span>
              {f.role && (
                <span style={{ marginLeft: 6, fontSize: 12 }}>
                  — used as {f.role === "subject" ? "the subject's own sheet" : "your comparable sales"}
                </span>
              )}
              {typeof f.pages_total === "number" && f.pages_total > 0 && (
                <div style={{ fontSize: 12, color: short ? colors.caution : colors.textSecondary }}>
                  Read {f.pages_read ?? 0} of {f.pages_total} page{f.pages_total === 1 ? "" : "s"}
                  {f.scanned ? " — no text layer, so the pages were read as images" : ""}
                  {short ? ". Anything on the pages we couldn't read is not in this report." : "."}
                </div>
              )}
              {f.stage_note && (
                <div style={{ fontSize: 12, marginTop: 2 }}>{f.stage_note}</div>
              )}
              {f.note && (
                <div style={{ fontSize: 12, color: colors.caution, marginTop: 2 }}>{f.note}</div>
              )}
              {f.read_ok === false && (
                <div style={{ fontSize: 12, color: colors.caution, marginTop: 2 }}>
                  Nothing readable came out of this file. Nothing from it is in the report.
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {thirdParty && (
        // Stated once, plainly, under the per-file lines rather than buried in one of them — the
        // agent's obligation is to their client, and this is the sentence they may need to repeat.
        <p style={{ margin: "10px 0 0", fontSize: 12, color: colors.textSecondary, lineHeight: 1.55 }}>
          A document-conversion service was used to read at least one of these files, which means it
          left our servers. The provider was instructed not to retain it, and the file was deleted
          immediately after conversion rather than kept.
        </p>
      )}
    </section>
  );
}
